import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { CssBaseline, ThemeProvider } from '@mui/material';
import * as Sentry from '@sentry/react';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { HttpStatusCode, PejHttpError } from '@common-lib';

import {
  configurationClient,
  MerchantPublicConfigurationModel,
} from '@merchant-clients/configuration';

import {
  KeyValueStore,
  MetadataContextState,
  MetadataProvider,
} from '@frontend/utils';
import { Loading } from '@frontend/views';

import { merchantPublicConfigurationCache } from './cache';
import { MerchantPublicConfigurationContext } from './merchant-public-configuration-context';
import { PortalConfigurationError } from './merchant-public-configuration-error.component';
import { createMerchantTheme } from './theme';
import monospaceFontUrl from './theme/fonts/AtkinsonHyperlegibleMono-VariableFont_wght.ttf';
import { ThemeModeProvider, useThemeMode } from './theme-mode-context';

declare module '@mui/material/styles' {
  interface AppSpecificStyles {}
}

type ErrorTypes = 'NOT_FOUND' | 'LOCKED' | 'FAILED_TO_INITIALIZE' | 'UNKNOWN';

export const MerchantPublicConfigurationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <ThemeModeProvider>
      <MerchantPublicConfigurationProviderInner>
        {children}
      </MerchantPublicConfigurationProviderInner>
    </ThemeModeProvider>
  );
};

const MerchantPublicConfigurationProviderInner = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [configuration, setConfiguration] = useState<
    MerchantPublicConfigurationModel | undefined
  >(undefined);

  const [loadError, setLoadError] = useState<ErrorTypes | undefined>();
  const [metadata, setMetadata] = useState<MetadataContextState | undefined>();
  const { mode } = useThemeMode();

  const theme = useMemo(() => createMerchantTheme(mode), [mode]);

  useEffectOnce(() => {
    merchantPublicConfigurationCache
      .get()
      .then((cached) => {
        if (cached && cached.appVersionHash === __APP_VERSION_HASH__) {
          setConfiguration(cached);
        }
      })
      .then(() => {
        configurationClient
          .getPublic()
          .then((response) => {
            return merchantPublicConfigurationCache
              .set({
                ...response,
                appVersionHash: __APP_VERSION_HASH__,
              })
              .then(() => setConfiguration(response));
          })
          .catch((error) => {
            if (error instanceof PejHttpError) {
              if (error.statusCode === HttpStatusCode.NOT_FOUND) {
                setLoadError('NOT_FOUND');
                return;
              } else if (error.statusCode === HttpStatusCode.LOCKED) {
                setLoadError('LOCKED');
                return;
              }
            }

            setLoadError('UNKNOWN');
          });
      })
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });
  });

  const posthog = usePostHog();

  useEffect(() => {
    if (!configuration) {
      return;
    }

    Promise.all([
      Device.getInfo(),
      Device.getLanguageTag(),
      CapacitorUpdater.getDeviceId(),
      Capacitor.isNativePlatform() ? App.getInfo() : Promise.resolve(undefined),
      KeyValueStore.get('anon_device_id'),
    ])
      .then(
        ([deviceInfo, languageTag, { deviceId }, appInfo, anonDeviceId]) => {
          // It is a bonus to re-use the capgo deviceId but it is not available
          // for web so we generate one ourselves. They have a very similar
          // implementation, the difference is that they store it using native
          // storage methods and we use localstorage.
          if (!deviceId) {
            if (!anonDeviceId) {
              anonDeviceId = crypto.randomUUID();
              KeyValueStore.set('anon_device_id', anonDeviceId);
            }
          }

          const version = Capacitor.isNativePlatform()
            ? appInfo!.version
            : APP_CONFIG.version;

          const newMetadata: MetadataContextState = {
            deviceId,
            appId: appInfo?.id ?? 'web',
            appName: appInfo?.name ?? 'web',
            appBuild: appInfo?.build ?? 'web',
            appVersion: version,
            url: APP_CONFIG.baseUrl
              ? APP_CONFIG.baseUrl
              : window.location.origin,
            environment: APP_CONFIG.environment,
            stack: APP_CONFIG.stack,
            osVersion: deviceInfo.osVersion,
            androidSDK: deviceInfo.androidSDKVersion,
            manufacturer: deviceInfo.manufacturer,
            model: deviceInfo.model,
            platform: deviceInfo.platform,
            languageTag: languageTag.value,
          };

          setMetadata(newMetadata);

          posthog.identify(posthog.get_distinct_id(), {
            // Only send properties that are not user specific. This allows
            // anonymous analytics for apps.
            anonymous_device_id: newMetadata.deviceId,
            app_id: newMetadata.appId,
            app_name: newMetadata.appName,
            app_build: newMetadata.appBuild,
            app_version: newMetadata.appVersion,
            url: newMetadata.url,
            environment: newMetadata.environment,
            stack: newMetadata.stack,
            $os_version: newMetadata.osVersion,
            android_sdk: newMetadata.androidSDK,
            manufacturer: newMetadata.manufacturer,
            model: newMetadata.model,
            platform: newMetadata.platform,
            language_tag: newMetadata.languageTag,
          });

          Sentry.setTags({
            app_id: newMetadata.appId,
            app_name: newMetadata.appName,
            app_build: newMetadata.appBuild,
            app_version: newMetadata.appVersion,
          });
        },
      )
      .catch((error) => {
        console.error(error);
        Sentry.captureException(
          new Error('Failed to initialize app', {
            cause: error,
          }),
          {
            level: 'fatal',
          },
        );
        setLoadError('FAILED_TO_INITIALIZE');
      });
  }, [configuration, posthog]);

  return (
    <CssBaseline>
      <link rel="icon" type="image/x-icon" href={'/assets/favicon.ico'} />
      <style>
        {`@font-face {
          font-family: 'Atkinson Hyperlegible Mono';
          font-style: 'normal';
          font-display: 'swap';
          src: url(${monospaceFontUrl}) format('truetype');
        }`}
      </style>

      {loadError === 'NOT_FOUND' && (
        <PortalConfigurationError errorMessage="App not found" />
      )}
      {loadError === 'LOCKED' && (
        <PortalConfigurationError errorMessage="App has been disabled. Please try again later." />
      )}
      {loadError === 'FAILED_TO_INITIALIZE' && (
        <PortalConfigurationError errorMessage="Failed to initialize app" />
      )}

      {(loadError === 'UNKNOWN' || loadError === undefined) && (
        <>
          {configuration && metadata ? (
            <MerchantPublicConfigurationContext.Provider value={configuration}>
              <MetadataProvider value={metadata}>
                <ThemeProvider theme={theme}>
                  <CssBaseline enableColorScheme>{children}</CssBaseline>
                </ThemeProvider>
              </MetadataProvider>
            </MerchantPublicConfigurationContext.Provider>
          ) : loadError ? (
            <PortalConfigurationError errorMessage="Failed to load app configuration" />
          ) : (
            <Loading />
          )}
        </>
      )}
    </CssBaseline>
  );
};
